import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { DateTimeInput } from 'semantic-ui-calendar-react'
import { Segment, Form, Tab, TabPane } from 'semantic-ui-react'
import SurveyService from '~/api/services/survey'
import { FormFields } from '~/components/FormFields'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { useAuth } from '~/routes/components/AuthProvider'
import { RouteTypes } from '~/routes/constants'
import { convertDate, getDateWithTime, getMinDate, handleErrors } from '~/utils/helpers'
import { StyledMenuItem } from '../../CloudStorage/style'
import { getError } from '~/components/FormFields/util'
import { StyledErrorLabel } from '../../MilkMoney/MilkDelivery/ShowTours/style'

const RecreateSurvey: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [isBusy, setIsBusy] = useState(false)
  const { handleSubmit, control, formState, setValue, watch, formState: { isSubmitted } } = useForm({
    mode: 'onSubmit'
  })
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const [activeIndex, setActiveIndex] = useState<string | number>(0)

  const watchStartDate = watch('start')
  const watchEndDate = watch('end')

  useEffect(() => {
    const languagesNew = []
    languages.map((language) => {
      languagesNew.push({
        id: language.id,
        title: '',
        description: ''
      })
    })
    setValue('languages', languagesNew)
  }, [])

  useEffect(() => {
    if (formState.isSubmitted) {
      if (Object.keys(formState.errors).length > 0) {
        notify(t('Please fill in all required fields'), 'error')
      }
    }

  }, [formState.isSubmitted])

  function getMenuItem(pane, index) {
    const languagesErrors = formState.errors?.languages;
    const paneHasError = index in (languagesErrors || {});

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  async function onSubmit(data) {
    setIsBusy(true);
    try {
      data.start = convertDate(watchStartDate)
      data.end = convertDate(watchEndDate)
      await SurveyService.recreateSurvey(id, data)
      notify(t('Successfully recreated survey'), 'success')
      navigate(RouteTypes.SURVEY)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderLanguagesForm() {
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
        panes={languages?.map((language, languageIndex) => {
          return {
            menuItem: getMenuItem(language.label, languageIndex),
            pane: <TabPane
              //@ts-ignore
              active={languageIndex === activeIndex}
              style={{ border: 'none' }}
              //@ts-ignore
              key={language?.id}
            >
              <FormFields.Input
                control={control}
                name={`languages[${languageIndex}].title`}
                label={t('Title')}
                placeholder={t('Title')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter title')
                  }
                }}
              />
              <FormFields.TextArea
                control={control}
                name={`languages[${languageIndex}].description`}
                label={t('Description')}
                placeholder={t('Description')}
              />
            </TabPane>
          };
        })}
      />
    );
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function renderForm() {
    return <>
      <Form.Group widths={'equal'}>
        <Controller
          control={control}
          name={'start'}
          rules={{
            required: {
              value: true,
              message: t('Please enter start date')
            },
            validate: () => {
              const dateFrom = getDateWithTime(watchStartDate)
              const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
              if (dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0) {
                return t('Please select a date ahead of the current date.')
              }
              return true
            }
          }}
          render={({ field, fieldState }) => {
            const dateFrom = watchStartDate ? getDateWithTime(watchStartDate) : ''
            const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
            return <Form.Field
              required
              error={!watchStartDate && isSubmitted ? getError(fieldState) : dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 ? true : false}
            >
              <label>{t('Start date')}</label>
              <DateTimeInput
                name='dateTime'
                placeholder={t('Start date')}
                minDate={dayjs(new Date()).format('DD.MM.YYYY HH:mm')}
                closable
                onKeyDown={handleKeyPress}
                markColor={'blue'}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateTimeFormat={'DD.MM.YYYY HH:mm'}
                className={'datePicker'}
                value={watchStartDate}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('start', value)}
              />
              {!watchStartDate && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter start date')}</StyledErrorLabel>}
              {dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 &&
                <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the current date.')}</StyledErrorLabel>}
            </Form.Field>

          }}
        />
        <Controller
          control={control}
          name={'end'}
          rules={{
            required: {
              value: true,
              message: t('Please enter end date')
            },
            validate: () => {
              const dateFrom = getDateWithTime(watchStartDate)
              const dateTo = getDateWithTime(watchEndDate)
              if (dateFrom && dateTo && dayjs(dateTo).diff(dayjs(dateFrom)) <= 0) {
                return t('Please select a date ahead of the start date.')
              }
              return true
            }
          }}
          render={({ field, fieldState }) => {
            const dateFrom = getDateWithTime(watchStartDate || '')
            const dateTo = getDateWithTime(watchEndDate || '')
            return <Form.Field
              required
              error={!watchEndDate && isSubmitted ? getError(fieldState) : false}
            >
              <label>{t('End date')}</label>
              <DateTimeInput
                name='dateTime'
                className={'datePicker'}
                placeholder={t('End date')}
                closable
                markColor={'blue'}
                onKeyDown={handleKeyPress}
                onKeyPress={handleKeyPress}
                initialDate={getMinDate(watchStartDate)}
                minDate={getMinDate(watchStartDate)}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateTimeFormat={'DD.MM.YYYY HH:mm'}
                disabled={!watchStartDate}
                value={watchEndDate}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('end', value)}
              />
              {!watchEndDate && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter end date')}</StyledErrorLabel>}
              {dateFrom && dateTo && dayjs(dateTo).diff(dayjs(dateFrom)) <= 0 &&
                <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the start date.')}</StyledErrorLabel>}
            </Form.Field>

          }}
        />
      </Form.Group >
    </>
  }

  return <Layout
    isBusy={isBusy}
    segmented
    close={RouteTypes.SURVEY}
    action={t('Recreate')}
    onSubmit={handleSubmit(onSubmit)}
    title={t('Recreate survey')}
  >
    {!isBusy && <>
      <Segment>
        {renderLanguagesForm()}
      </Segment>
      <Segment style={{ marginTop: '3em' }}>
        {renderForm()}
      </Segment>
    </>
    }

  </Layout>
}
export default RecreateSurvey